/**
 * TechCheckScreen — logique pure (détection appareil + sonde connexion),
 * isolée du JSX pour rester testable et réutilisable. `sampleOnce` /
 * `computeProbeStats` sont repris tels quels par `TestScreen` pour la
 * mesure PASSIVE (1 échantillon par carte pendant le test, cf. CONTRACT
 * §3/§4) — même méthode que la sonde EXPLICITE de l'étape 0, juste une
 * cadence différente côté appelant.
 */
import { median } from "../format";
import { candidateApi } from "../api";
import type { DeviceClass } from "../types";

/**
 * Détecte la classe d'appareil côté client : `pointer:fine` + `hover:hover`
 * (souris) → 'computer'. Sinon, heuristique UA pour distinguer tablette de
 * mobile. Défensif (SSR / navigateurs exotiques) : retombe sur 'computer'
 * si `matchMedia`/`navigator` sont indisponibles plutôt que de bloquer.
 * Détection SOUPLE — jamais utilisée pour bloquer le parcours (cf. mission :
 * « mobile passe, on marque »).
 */
export function detectDeviceClass(): DeviceClass {
  if (typeof window === "undefined" || typeof navigator === "undefined") return "computer";
  const pointerFine = window.matchMedia?.("(pointer: fine)").matches ?? true;
  const hoverHover = window.matchMedia?.("(hover: hover)").matches ?? true;
  if (pointerFine && hoverHover) return "computer";
  const ua = navigator.userAgent || "";
  const isTablet =
    /iPad|Tablet|PlayBook|Nexus 7|Nexus 10/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua));
  return isTablet ? "tablet" : "mobile";
}

/** 12 échantillons × 2 s ≈ 24 s — colle au « ~25 s » de la maquette. */
export const PROBE_SAMPLE_COUNT = 12;
export const PROBE_SAMPLE_INTERVAL_MS = 2000;
const PING_TIMEOUT_MS = 4000;

export interface ProbeStats {
  rtt_median_ms: number;
  rtt_p95_ms: number;
  dropouts_pct: number;
}

function percentile95(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.ceil(0.95 * sorted.length) - 1));
  return sorted[idx];
}

/** Un aller-retour chronométré côté client vers `candidateApi.ping`. `null`
 *  = coupure (erreur réseau ou timeout > 4 s) — compte comme un dropout. */
export async function sampleOnce(token: string): Promise<number | null> {
  const start = performance.now();
  try {
    await Promise.race([
      candidateApi.ping(token),
      new Promise((_resolve, reject) => {
        setTimeout(() => reject(new Error("ping timeout")), PING_TIMEOUT_MS);
      }),
    ]);
    return performance.now() - start;
  } catch {
    return null;
  }
}

/**
 * Réduit un jeu d'échantillons (`null` = coupure) en stats prêtes pour
 * `POST /probe`. `null` si la liste est vide (rien à envoyer — cf.
 * `TestScreen`, mesure passive best-effort). Si TOUS les échantillons ont
 * échoué, renvoie un pire-cas explicite plutôt que `0 ms` trompeur.
 */
export function computeProbeStats(samples: Array<number | null>): ProbeStats | null {
  if (samples.length === 0) return null;
  const valid = samples.filter((s): s is number => s !== null);
  const dropouts_pct = Math.round(((samples.length - valid.length) / samples.length) * 1000) / 10;
  if (valid.length === 0) {
    return { rtt_median_ms: PING_TIMEOUT_MS, rtt_p95_ms: PING_TIMEOUT_MS, dropouts_pct: 100 };
  }
  return {
    rtt_median_ms: Math.round(median(valid) ?? 0),
    rtt_p95_ms: Math.round(percentile95(valid)),
    dropouts_pct,
  };
}
